import * as React from "react";
import { lazy, ComponentType } from "react";

// Generic lazy loading wrapper with error boundary
export const createLazyComponent = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  fallback?: ComponentType
) => {
  const LazyComponent = lazy(importFunc);

  return (props: any) =>
    React.createElement(
      React.Suspense,
      {
        fallback: fallback
          ? React.createElement(fallback)
          : React.createElement("div", null, "Loading..."),
      },
      React.createElement(LazyComponent, props)
    );
};

// Preload components for better performance
export const preloadComponent = (importFunc: () => Promise<any>) => {
  return () => {
    importFunc();
    return null;
  };
};

// Route-based code splitting
export const createRouteComponent = <T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  routeName: string
) => {
  const LazyComponent = lazy(importFunc);

  // Preload on hover
  const preload = () => {
    importFunc();
  };

  return {
    Component: LazyComponent,
    preload,
    routeName,
  };
};

// Dynamic imports with retry logic
export const importWithRetry = async <T>(
  importFunc: () => Promise<T>,
  maxRetries: number = 3
): Promise<T> => {
  let lastError: Error | null = null;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await importFunc();
    } catch (error) {
      lastError = error as Error;
      console.warn(`Import attempt ${i + 1} failed:`, error);

      if (i < maxRetries - 1) {
        // Wait before retry with exponential backoff
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i) * 1000)
        );
      }
    }
  }

  throw lastError;
};

// Bundle analyzer utility
export const analyzeBundle = () => {
  if (process.env.NODE_ENV === "development") {
    // This would typically integrate with webpack-bundle-analyzer
    console.log("Bundle analysis available in development mode");
  }
};

// Performance budget checker
export const checkPerformanceBudget = (
  componentName: string,
  renderTime: number
) => {
  const budgets = {
    ProviderListPage: 50, // 50ms budget
    ProviderDetailPage: 30, // 30ms budget
    AdminProviderManagement: 100, // 100ms budget
    default: 16, // 16ms budget (60fps)
  };

  const budget =
    budgets[componentName as keyof typeof budgets] || budgets.default;

  if (renderTime > budget) {
    console.warn(
      `Performance budget exceeded for ${componentName}: ${renderTime}ms > ${budget}ms`
    );
    return false;
  }

  return true;
};
